import { cancelPageReads } from '@/utils/teacherReads'
import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const MainLayout = () => import('@/components/common/AppLayout.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/DashboardPage.vue'),
        meta: { permission: 'dashboard:view', keepAlive: true },   // 所有登录用户
      },
      {
        path: 'classes',
        name: 'Classes',
        component: () => import('@/views/ClassPage.vue'),
        meta: { permission: 'class:view', keepAlive: true },
      },
      {
        path: 'students',
        name: 'Students',
        component: () => import('@/views/StudentPage.vue'),
        meta: { permission: 'student:read', keepAlive: true },
      },
      {
        path: 'questions',
        name: 'Questions',
        component: () => import('@/views/ResourceHubPage.vue'),
        meta: { permission: 'question:read', keepAlive: true },
      },
      {
        path: 'questions/vlm-review',
        name: 'VlmReview',
        component: () => import('@/views/VlmImportReview.vue'),
        meta: { permission: 'question:import' },
      },
      {
        path: 'paper-assembly',
        name: 'PaperAssembly',
        component: () => import('@/views/PaperAssemblyPage.vue'),
        meta: { permission: 'homework:create' },
      },
      {
        path: 'homeworks',
        name: 'Homeworks',
        component: () => import('@/views/HomeworkPage.vue'),
        meta: { permission: 'homework:read', keepAlive: true },
      },
      {
        path: 'homeworks/:id',
        name: 'HomeworkDetail',
        component: () => import('@/views/HomeworkDetailPage.vue'),
        meta: { permission: 'homework:read' },
      },
      {
        path: 'corrections',
        name: 'Corrections',
        component: () => import('@/views/GradingPage.vue'),
        meta: { permission: 'grading:own' },
      },
      {
        path: 'grading/review',
        name: 'GradingWorkspace',
        component: () => import('@/views/GradingWorkspace.vue'),
        meta: { permission: 'grading:own' },
      },
      {
        path: 'essay-grading',
        name: 'EssayGrading',
        component: () => import('@/views/EssayGradingPage.vue'),
        meta: { permission: 'grading:own' },
      },
      {
        path: 'errors',
        name: 'Errors',
        component: () => import('@/views/ErrorPage.vue'),
        meta: { permission: 'error:read', keepAlive: true },
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@/views/AnalyticsPage.vue'),
        meta: { permission: 'analytics:class', keepAlive: true },
      },
      {
        path: 'risk-warnings',
        name: 'RiskWarnings',
        component: () => import('@/views/RiskWarningPage.vue'),
        meta: { permission: 'analytics:class' },
      },
      {
        path: 'prerequisites',
        name: 'Prerequisites',
        component: () => import('@/views/PrerequisitePage.vue'),
        meta: { permission: 'question:read' },
      },
      {
        path: 'crossmodule',
        name: 'CrossSubject',
        component: () => import('@/views/CrossModulePage.vue'),
        meta: { permission: 'question:read' },
      },
      {
        path: 'smart-engine',
        name: 'SmartEngine',
        component: () => import('@/views/SmartEnginePage.vue'),
        meta: { permission: 'question:read' },
      },
      {
        path: 'hardware-devices',
        name: 'HardwareDevices',
        component: () => import('@/views/HardwareDevicesView.vue'),
        meta: { permission: 'device:read' },
      },
      {
        path: 'future-class/dashboard',
        name: 'FutureClassDashboard',
        component: () => import('@/views/FutureClassDashboard.vue'),
        meta: { permission: 'dashboard:view' },
      },
      {
        path: 'future-class/prepare',
        name: 'FutureClassPrepare',
        component: () => import('@/views/FutureClassPrepare.vue'),
        meta: { permission: 'dashboard:view' },
      },
      {
        path: 'future-class/prepare/:chapterCode',
        name: 'FutureClassPrepareChapter',
        component: () => import('@/views/FutureClassPrepare.vue'),
        meta: { permission: 'dashboard:view' },
      },
      {
        path: 'future-class/classroom/:sessionId',
        name: 'FutureClassClassroom',
        component: () => import('@/views/FutureClassClassroom.vue'),
        meta: { permission: 'dashboard:view' },
      },
      {
        path: 'future-class',
        redirect: '/future-class/dashboard',
      },
      {
        path: 'geometry',
        name: 'GeometryWorkspace',
        component: () => import('@/views/GeometryWorkspacePage.vue'),
        meta: { permission: 'dashboard:view' },
      },
      // Phase 1 收敛: 旧实验室入口重定向到统一工作台 (CognitiveLab 组件保留,
      // Phase 4 作为 Cognitive Layer 接回)
      {
        path: 'geometry-lab',
        redirect: '/geometry',
      },
      // 智阅工作台 — 直接进入统一批改/复核工作区 (GradingWorkspace)
      {
        path: 'teacher',
        redirect: '/grading/review',
        meta: { permission: 'grading:own' },
      },
      {
        path: 'teacher/dashboard',
        redirect: { name: 'GradingWorkspace' },   // 原 TeacherDashboard 页已移除 → 直达复核工作区
        meta: { permission: 'grading:own' },
      },
      // ★ 2026-08-19 合并: 一键批改(wizard) + 复核队列(review) → 同一三阶段页面 GradingWorkspace
      //   wizard 无 homework_id → 上传页; review 带 homework_id → 直达复核; 上传完成后同页流转复核
      {
        path: 'teacher/grading-wizard',
        name: 'OneClickGrading',
        component: () => import('@/views/GradingWorkspace.vue'),
        meta: { permission: 'grading:own' },
      },
      {
        path: 'teacher/review',
        redirect: { name: 'GradingWorkspace' },
      },
      {
        path: 'teacher/calibration',
        redirect: '/v2/corrections',
      },
      {
        path: 'teacher/student-profile',
        name: 'StudentCognitiveProfile',
        component: () => import('@/views/StudentCognitiveProfile.vue'),
        meta: { permission: 'analytics:class' },
      },
      {
        path: 'school',
        name: 'SchoolDashboard',
        component: () => import('@/views/SchoolDashboard.vue'),
        meta: { permission: 'analytics:class' },
      },
    ],
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  // ─── ★ 2026-08-18 Frontend Capability Recovery (DUAL_TRACK_FRONTEND Phase 1A) ───
  // 6 个此前"功能完整但路由未接"的页面恢复入口。统一 requiresAdmin=true 先让
  // 管理员可见 (Pending Browser Gate: 逐页验证数据加载/权限/后端状态后再
  // 决定是否开放给非管理员)。hidden=false 表示出现在导航。
  {
    path: '/os-console-v2',
    name: 'OsConsole',
    component: () => import('@/views/OsConsolePage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hidden: false, title: 'OS 驾驶舱' },
  },
  {
    path: '/ai-trust-center',
    name: 'AiTrustCenter',
    component: () => import('@/views/AiTrustCenterPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hidden: false, title: 'AI 信任中心' },
  },
  {
    path: '/bkt-management',
    name: 'BktManagement',
    component: () => import('@/views/BktManagementPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hidden: false, title: 'BKT 参数管理' },
  },
  {
    path: '/data-governance',
    name: 'DataGovernance',
    component: () => import('@/views/DataGovernancePage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hidden: false, title: '数据治理' },
  },
  {
    path: '/ops-tuning',
    name: 'OpsTuning',
    component: () => import('@/views/OpsTuningPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hidden: false, title: '运维调优' },
  },
  {
    path: '/knowledge-graph',
    name: 'KnowledgeGraph',
    component: () => import('@/views/KnowledgeGraphPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hidden: false, title: '知识图谱诊断' },
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/essay-grading/print',
    name: 'EssayPrintReport',
    component: () => import('@/views/EssayPrintReport.vue'),
    meta: { requiresAuth: true, permission: 'grading:own' },
  },
  {
    path: '/student-paper-print',
    name: 'StudentPaperPrint',
    component: () => import('@/views/StudentPaperPrint.vue'),
    meta: { requiresAuth: true, permission: 'homework:read' },
  },
  {
    path: '/lesson-show/:id',
    name: 'LessonShow',
    component: () => import('@/views/LessonShowPage.vue'),
    meta: { requiresAuth: false },
  },
  // 404 fallback
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/LoginView.vue'),
    beforeEnter: (_to, _from, next) => {
      next('/login')
    },
  },
]

const router = createRouter({
  history: createWebHistory('/v2/'),
  routes,
})

// ─── 外部页面白名单（这些路径由后端直接返回独立 HTML，不走 SPA）───
const EXTERNAL_PAGES = ['/terminal', '/terminal_device_management']

// ─── ★ Auth v2 全局路由守卫 ──────────────────────────
// 真相源唯一: HttpOnly JWT Cookie + GET /api/auth/session
// 禁止: localStorage['logged_in']
// 登录页始终可访问; 受保护页面由 session.authenticated 决策
router.beforeEach(async (to, _from, next) => {
  if (to.fullPath !== _from.fullPath) cancelPageReads()
  // 外部页面：跳出 SPA，由浏览器直接请求后端
  if (EXTERNAL_PAGES.includes(to.path)) {
    window.location.replace(to.fullPath)
    return
  }

  const authStore = useAuthStore()

  // 1) 免认证页面 — /login 及其变体始终放行
  if (to.meta.requiresAuth === false) {
    next()
    return
  }

  // 2) ★ Auth v2: 受保护页面 → 先查 session
  //    如果 session 未初始化 → 调用 initialize() 请求 /api/auth/session
  if (!authStore.scopeLoaded) {
    const ok = await authStore.initialize()
    if (!ok) {
      // session 无效 → 跳转登录页，记录来源
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }
  }

  // 3) 管理员页面
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next({ name: 'Forbidden', query: { from: to.fullPath } })
    return
  }

  // 4) RBAC 权限检查
  const requiredPermission = to.meta.permission as string | undefined
  if (requiredPermission && !authStore.hasPermission(requiredPermission)) {
    next({ name: 'Forbidden', query: { from: to.fullPath } })
    return
  }

  // 5) 已认证 + 权限匹配 → 放行
  next()
})

export default router
